import React, { useState, useCallback, useRef } from "react";

export default function AICardGameTrainer() {
  // AI学习相关状态
  const [trainEpisodes, setTrainEpisodes] = useState(500);
  const [isAITraining, setIsAITraining] = useState(false);
  const [isAIPlaying, setIsAIPlaying] = useState(false);
  const [trainStyle, setTrainStyle] = useState("balanced");
  const trainingRef = useRef(false);
  const playingRef = useRef(false);

  const [qTable, setQTable] = useState(() => {
    const initialBrain = {};
    for (let i = 0; i <= 10; i++) {
      initialBrain[i] = { 结束游戏: 0, 继续翻牌: 0 };
    }
    return initialBrain;
  });

  const [aiStats, setAIStats] = useState({
    episode: 0,
    totalEpisodes: 0,
    currentScore: 0,
    bestScore: 0,
    recentScores: [],
    explorationHistory: [],
    recentFailures: [], // 记录最近的失败情况
    adaptiveCautionLevel: 0, // 适应性谨慎程度
  });

  const [aiGameState, setAIGameState] = useState({
    currentCard: "",
    currentScore: 0,
    gameStatus: "waiting",
    gameHistory: [],
  });

  const cards = ["A", 2, 3, 4, 5, 6, 7, 8, 9, 10, "J", "Q", "K"];

  // AI学习参数 - 适应性勇敢版
  const getTrainingParams = () => {
    if (trainStyle === "bold") {
      return {
        EPSILON: 0.98, // 保持高初始探索率
        ALPHA: 0.25, // 保持快速学习
        GAMMA: 0.99, // 保持重视未来奖励
        DECAY: 0.9992, // 稍微慢一点的衰减
        MIN_EPSILON: 0.4, // 保持最小探索率
        HIGH_SCORE_BOOST: 0.5, // 保持高分探索加成
        HIGH_SCORE_REWARD: 1, // 保持高分奖励
        PROGRESSIVE_REWARD: 0.5, // 保持渐进奖励
        BOLD_THRESHOLD: 6, // 勇敢阈值
        FAILURE_PENALTY: -200, // 增加失败惩罚！
        CAUTION_DECAY: 0.8, // 谨慎程度衰减率
        MAX_CAUTION: 0.6, // 最大谨慎程度
        FAILURE_MEMORY: 10, // 记住最近10次的失败情况
      };
    } else {
      return {
        EPSILON: 0.9,
        ALPHA: 0.15,
        GAMMA: 0.95,
        DECAY: 0.998,
        MIN_EPSILON: 0.2,
        HIGH_SCORE_BOOST: 0.3,
        HIGH_SCORE_REWARD: 1,
        PROGRESSIVE_REWARD: 0.2,
        BOLD_THRESHOLD: 8,
        FAILURE_PENALTY: -200, // 平衡模式保持原来的惩罚
        CAUTION_DECAY: 0.98,
        MAX_CAUTION: 0.4,
        FAILURE_MEMORY: 5,
      };
    }
  };

  const getCardValue = (card) => {
    if (card === "A") return 1;
    if (card === "J") return 11;
    if (card === "Q") return 12;
    if (card === "K") return 13;
    return card;
  };

  const getCardColor = (card) => {
    const redCards = ["A", "J", "Q", "K"];
    return redCards.includes(card) ? "text-red-600" : "text-black";
  };

  const chooseAction = useCallback(
    (state, epsilon) => {
      const brainData = qTable[state];
      const params = getTrainingParams();

      // 检查是否已经学习过这个状态
      const hasLearned =
        brainData["结束游戏"] !== 0 || brainData["继续翻牌"] !== 0;

      // 如果epsilon=0（AI游戏模式），严格按照Q-table执行，不进行任何探索
      if (epsilon === 0) {
        if (hasLearned) {
          // 严格按照Q值选择最优动作
          const bestAction =
            brainData["结束游戏"] > brainData["继续翻牌"]
              ? "结束游戏"
              : "继续翻牌";
          console.log(
            `AI游戏模式-状态${state}: 结束=${brainData["结束游戏"]}, 翻牌=${brainData["继续翻牌"]}, 选择: ${bestAction}`
          );
          return bestAction;
        } else {
          // 完全没学过，随机选择
          console.log(`AI游戏模式-状态${state}: 未学习过，随机选择`);
          return Math.random() < 0.5 ? "结束游戏" : "继续翻牌";
        }
      }

      // 训练模式：适应性勇敢策略
      let adjustedEpsilon = epsilon;

      // 计算当前的谨慎程度（基于最近失败）
      const cautionAdjustment = aiStats.adaptiveCautionLevel;

      // 对高分区域的探索策略 - 现在考虑谨慎程度
      if (state >= params.BOLD_THRESHOLD) {
        // 基础勇敢加成，但会被谨慎程度抵消
        const baseBoost = params.HIGH_SCORE_BOOST;
        const adjustedBoost = baseBoost * (1 - cautionAdjustment);
        adjustedEpsilon = Math.min(0.95, epsilon + adjustedBoost);
      }

      // 特别激进：8分以上时的策略，但要考虑失败经验
      if (trainStyle === "bold" && state >= 8) {
        const maxBoost = 0.9;
        const cautionPenalty = cautionAdjustment * 0.7; // 失败经验的影响
        const finalBoost = maxBoost * (1 - cautionPenalty);
        adjustedEpsilon = Math.min(0.98, epsilon + finalBoost);
      }

      if (Math.random() < adjustedEpsilon && hasLearned) {
        // 探索模式：适应性策略
        if (state >= params.BOLD_THRESHOLD) {
          // 高分区域的探索策略 - 根据失败经验调整
          let continueProb;
          if (trainStyle === "bold") {
            // 勇敢模式：基础概率高，但失败后会降低
            const baseContinueProb = state >= 8 ? 0.95 : 0.9;
            const cautionReduction = cautionAdjustment * 0.4; // 失败经验降低继续概率
            continueProb = Math.max(0.5, baseContinueProb - cautionReduction);
          } else {
            const baseContinueProb = state >= 8 ? 0.8 : 0.7;
            const cautionReduction = cautionAdjustment * 0.3;
            continueProb = Math.max(0.4, baseContinueProb - cautionReduction);
          }
          return Math.random() < continueProb ? "继续翻牌" : "结束游戏";
        }
        // 中等分数时的策略
        const midScoreContinueProb =
          trainStyle === "bold"
            ? Math.max(0.5, 0.75 - cautionAdjustment * 0.3)
            : Math.max(0.4, 0.6 - cautionAdjustment * 0.2);
        return Math.random() < midScoreContinueProb ? "继续翻牌" : "结束游戏";
      } else if (hasLearned) {
        // 利用模式：严格按照Q值选择最优动作
        const bestAction =
          brainData["结束游戏"] > brainData["继续翻牌"]
            ? "结束游戏"
            : "继续翻牌";
        return bestAction;
      } else {
        // 完全没学过，给新手一点勇气，但也要考虑失败经验
        const newbieContinueProb =
          trainStyle === "bold"
            ? Math.max(0.5, 0.7 - cautionAdjustment * 0.2)
            : Math.max(0.4, 0.5 - cautionAdjustment * 0.1);
        return Math.random() < newbieContinueProb ? "继续翻牌" : "结束游戏";
      }
    },
    [qTable, trainStyle, aiStats.adaptiveCautionLevel]
  );

  const updateQTable = useCallback(
    (state, action, reward, nextState, isTerminal) => {
      const params = getTrainingParams();

      setQTable((prevBrain) => {
        const newBrain = { ...prevBrain };
        const currentValue = newBrain[state][action];
        let targetValue;

        let adjustedReward = reward;

        // 优化奖励机制
        if (!isTerminal && nextState >= params.BOLD_THRESHOLD) {
          adjustedReward += params.HIGH_SCORE_REWARD;
        }

        // 渐进式奖励 - 鼓励到达更高分数
        if (!isTerminal) {
          adjustedReward += nextState * params.PROGRESSIVE_REWARD;
        }

        // 特别奖励：到达9-10分给予额外奖励
        if (!isTerminal && nextState >= 9) {
          adjustedReward += trainStyle === "bold" ? 5 : 2;
        }

        if (isTerminal) {
          targetValue = adjustedReward;
        } else {
          const nextStateValues = newBrain[nextState];
          targetValue =
            adjustedReward +
            params.GAMMA *
              Math.max(
                nextStateValues["结束游戏"],
                nextStateValues["继续翻牌"]
              );
        }

        newBrain[state] = {
          ...newBrain[state],
          [action]: currentValue + params.ALPHA * (targetValue - currentValue),
        };

        return newBrain;
      });
    },
    [trainStyle]
  );

  const simulateAIGame = useCallback(async () => {
    let currentScore = 0;
    let steps = 0;
    const maxSteps = 20;
    const exploredStates = [];
    const params = getTrainingParams();
    let gameHadFailure = false; // 记录这局游戏是否失败

    const currentEpsilon = Math.max(
      params.MIN_EPSILON,
      params.EPSILON * Math.pow(params.DECAY, aiStats.episode)
    );

    while (steps < maxSteps && trainingRef.current) {
      exploredStates.push(currentScore);
      const action = chooseAction(currentScore, currentEpsilon);

      if (action === "结束游戏") {
        // 优化结束游戏的奖励
        let endReward = currentScore * 0.8; // 提高基础奖励

        // 高分结束给予额外奖励
        if (currentScore >= 8) {
          endReward += trainStyle === "bold" ? 8 : 5;
        } else if (currentScore >= 6) {
          endReward += trainStyle === "bold" ? 3 : 2;
        }

        updateQTable(currentScore, action, endReward, currentScore, true);
        break;
      } else {
        const card = cards[Math.floor(Math.random() * cards.length)];
        const value = getCardValue(card);
        const newScore = currentScore + (value % 3);

        if (newScore > 10) {
          // 失败！使用更严厉的惩罚
          gameHadFailure = true;
          updateQTable(currentScore, action, params.FAILURE_PENALTY, 0, true);
          currentScore = 0;
          break;
        } else {
          // 成功继续的奖励
          let continueReward = value % 3;

          // 到达高分区域的额外奖励
          if (newScore >= 8) {
            continueReward += trainStyle === "bold" ? 3 : 2;
          }

          updateQTable(currentScore, action, continueReward, newScore, false);
          currentScore = newScore;
        }
      }
      steps++;

      await new Promise((resolve) => setTimeout(resolve, 30));
    }

    // 更新失败记录和适应性谨慎程度
    setAIStats((prev) => {
      const newRecentFailures = [...prev.recentFailures, gameHadFailure].slice(
        -params.FAILURE_MEMORY
      );

      // 计算失败率
      const failureRate =
        newRecentFailures.filter((f) => f).length / newRecentFailures.length;

      // 根据失败率调整谨慎程度
      let newCautionLevel = prev.adaptiveCautionLevel;
      if (gameHadFailure) {
        // 失败后增加谨慎程度
        newCautionLevel = Math.min(params.MAX_CAUTION, newCautionLevel + 0.1);
      } else {
        // 成功后逐渐降低谨慎程度
        newCautionLevel = Math.max(0, newCautionLevel * params.CAUTION_DECAY);
      }

      return {
        ...prev,
        explorationHistory: [
          ...prev.explorationHistory,
          ...exploredStates,
        ].slice(-1000),
        recentFailures: newRecentFailures,
        adaptiveCautionLevel: newCautionLevel,
      };
    });

    return currentScore;
  }, [
    chooseAction,
    updateQTable,
    getCardValue,
    aiStats.episode,
    aiStats.adaptiveCautionLevel,
    cards,
    trainStyle,
  ]);

  const startAITraining = async () => {
    setIsAITraining(true);
    trainingRef.current = true;
    const totalEpisodes = trainEpisodes;

    setAIStats((prev) => ({
      ...prev,
      episode: 0,
      totalEpisodes,
      recentScores: [],
      explorationHistory: [],
    }));

    try {
      for (let episode = 0; episode < totalEpisodes; episode++) {
        if (!trainingRef.current) break;

        const finalScore = await simulateAIGame();

        setAIStats((prev) => ({
          ...prev,
          episode: episode + 1,
          totalEpisodes,
          currentScore: finalScore,
          bestScore: Math.max(prev.bestScore, finalScore),
          recentScores: [...prev.recentScores.slice(-9), finalScore],
        }));

        if ((episode + 1) % 10 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }
    } catch (error) {
      console.error("AI训练出错:", error);
    } finally {
      setIsAITraining(false);
      trainingRef.current = false;
    }
  };

  const stopAITraining = () => {
    trainingRef.current = false;
    setIsAITraining(false);
  };

  const resetAI = () => {
    const freshBrain = {};
    for (let i = 0; i <= 10; i++) {
      freshBrain[i] = { 结束游戏: 0, 继续翻牌: 0 };
    }
    setQTable(freshBrain);
    setAIStats({
      episode: 0,
      totalEpisodes: 0,
      currentScore: 0,
      bestScore: 0,
      recentScores: [],
      explorationHistory: [],
      recentFailures: [],
      adaptiveCautionLevel: 0,
    });
    setAIGameState({
      currentCard: "",
      currentScore: 0,
      gameStatus: "waiting",
      gameHistory: [],
    });
  };

  const startAIGame = async () => {
    setIsAIPlaying(true);
    playingRef.current = true;

    const initialState = {
      currentCard: "",
      currentScore: 0,
      gameStatus: "playing",
      gameHistory: [],
    };
    setAIGameState(initialState);

    let currentScore = 0;
    let steps = 0;
    const maxSteps = 20;
    const gameHistory = [];

    try {
      while (steps < maxSteps && playingRef.current) {
        const action = chooseAction(currentScore, 0);

        gameHistory.push({ score: currentScore, action, step: steps + 1 });

        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (action === "结束游戏") {
          setAIGameState((prev) => ({
            ...prev,
            currentCard: "✓",
            currentScore: currentScore,
            gameStatus: "ended",
            gameHistory,
          }));
          break;
        } else {
          const card = cards[Math.floor(Math.random() * cards.length)];
          const value = getCardValue(card);
          const newScore = currentScore + (value % 3);

          gameHistory[gameHistory.length - 1].card = card;
          gameHistory[gameHistory.length - 1].newScore = newScore;

          setAIGameState((prev) => ({
            ...prev,
            currentCard: card,
            currentScore: newScore,
            gameStatus: newScore > 10 ? "failed" : "playing",
            gameHistory: [...gameHistory],
          }));

          if (newScore > 10) {
            currentScore = 0;
            break;
          } else {
            currentScore = newScore;
          }
        }
        steps++;
      }
    } catch (error) {
      console.error("AI游戏出错:", error);
    } finally {
      setIsAIPlaying(false);
      playingRef.current = false;
    }
  };

  const stopAIGame = () => {
    playingRef.current = false;
    setIsAIPlaying(false);
  };

  const getExplorationStats = () => {
    const stateVisits = {};
    for (let i = 0; i <= 10; i++) {
      stateVisits[i] = 0;
    }

    aiStats.explorationHistory.forEach((state) => {
      if (stateVisits[state] !== undefined) {
        stateVisits[state]++;
      }
    });

    return stateVisits;
  };

  const explorationStats = getExplorationStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-2">
          🤖 AI玩翻牌游戏
        </h1>
        <p className="text-center text-gray-600 mb-8">
          看看AI能否学会玩这个游戏
        </p>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">🎮 让AI玩游戏</h2>
            <div className="flex gap-3">
              <button
                onClick={startAIGame}
                disabled={isAITraining || isAIPlaying}
                className="py-2 px-4 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
              >
                {isAIPlaying ? "AI游戏中..." : "开始游戏"}
              </button>

              <button
                onClick={stopAIGame}
                disabled={!isAIPlaying}
                className="py-2 px-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
              >
                停止游戏
              </button>
            </div>
          </div>

          {isAIPlaying || aiGameState.gameStatus !== "waiting" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div
                    className={`w-32 h-44 border-3 border-gray-600 rounded-lg flex items-center justify-center text-4xl font-bold bg-white shadow-lg ${
                      aiGameState.currentCard
                        ? getCardColor(aiGameState.currentCard)
                        : "text-gray-400"
                    }`}
                  >
                    {aiGameState.currentCard || "?"}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    AI得分：{aiGameState.currentScore}
                  </div>
                  <div className="text-lg text-gray-600">
                    {aiGameState.gameStatus === "playing" && "🤔 AI正在思考..."}
                    {aiGameState.gameStatus === "ended" &&
                      `🎉 AI选择结束，得到${aiGameState.currentScore}分！`}
                    {aiGameState.gameStatus === "failed" &&
                      "💥 AI翻牌失败了！得到0分"}
                    {aiGameState.gameStatus === "waiting" &&
                      '点击"开始游戏"让AI试试'}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-3">
                  🎯 AI的游戏记录
                </h4>
                {aiGameState.gameHistory.length > 0 ? (
                  <div className="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                    <div className="space-y-2">
                      {aiGameState.gameHistory.map((record, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-2 bg-white rounded border text-sm"
                        >
                          <span className="font-medium">第{record.step}步</span>
                          <span className="text-gray-600">
                            当时{record.score}分
                          </span>
                          <span className="font-semibold">
                            {record.action === "结束游戏"
                              ? "🏁 选择结束"
                              : record.card
                              ? `🎲 翻到${record.card} → ${record.newScore}分`
                              : "🎲 正在翻牌..."}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
                    还没有游戏记录，让AI开始游戏吧！
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-8xl mb-4">🎯</div>
              <h3 className="text-2xl font-semibold text-gray-700 mb-2">
                让AI试试这个游戏
              </h3>
              <p className="text-gray-500 mb-4">
                AI还没有经验，看看它会怎么玩？
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">🤖 训练AI</h2>

            <div
              className={`p-4 rounded-lg mb-4 ${
                aiStats.bestScore >= 8
                  ? "bg-green-50 border border-green-200"
                  : aiStats.bestScore >= 5
                  ? "bg-yellow-50 border border-yellow-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="text-lg font-semibold">
                AI最佳成绩：{aiStats.bestScore}分
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择训练风格：
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setTrainStyle("balanced")}
                  disabled={isAITraining || isAIPlaying}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                    trainStyle === "balanced"
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="text-center">
                    <div className="font-semibold">🎯 平衡训练</div>
                    <div className="text-xs opacity-80">稳健保守</div>
                  </div>
                </button>

                <button
                  onClick={() => setTrainStyle("bold")}
                  disabled={isAITraining || isAIPlaying}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                    trainStyle === "bold"
                      ? "bg-red-500 text-white border-red-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="text-center">
                    <div className="font-semibold">🚀 冒险训练</div>
                    <div className="text-xs opacity-80">勇敢大胆</div>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                让AI练习多少次？
              </label>
              <div className="flex gap-2 items-center mb-2">
                <input
                  type="number"
                  min="10"
                  max="10000"
                  step="10"
                  value={trainEpisodes}
                  onChange={(e) =>
                    setTrainEpisodes(
                      Math.max(10, parseInt(e.target.value) || 10)
                    )
                  }
                  disabled={isAITraining || isAIPlaying}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-600">次</span>
              </div>
              <div className="flex gap-2">
                {[100, 500, 1000, 2000, 5000].map((num) => (
                  <button
                    key={num}
                    onClick={() => setTrainEpisodes(num)}
                    disabled={isAITraining || isAIPlaying}
                    className="px-3 py-1 text-sm bg-purple-100 hover:bg-purple-200 disabled:bg-gray-100 rounded"
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={startAITraining}
                disabled={isAITraining || isAIPlaying}
                className="w-full py-3 px-6 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
              >
                {isAITraining
                  ? `🏃 训练中... (${aiStats.episode}/${trainEpisodes})`
                  : `${
                      trainStyle === "bold" ? "🚀" : "🎯"
                    } 开始训练 (${trainEpisodes}次)`}
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={stopAITraining}
                  disabled={!isAITraining}
                  className="py-2 px-4 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  ⏹️ 停止训练
                </button>

                <button
                  onClick={resetAI}
                  disabled={isAITraining || isAIPlaying}
                  className="py-2 px-4 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  🗑️ 清空大脑
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-blue-50 p-3 rounded border">
                <div className="text-sm text-gray-600">练习进度</div>
                <div className="text-lg font-semibold text-blue-600">
                  {aiStats.episode}/{aiStats.totalEpisodes || trainEpisodes}
                </div>
                {aiStats.totalEpisodes > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-3 mt-1">
                    <div
                      className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${
                          (aiStats.episode / aiStats.totalEpisodes) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                )}
              </div>

              <div className="bg-green-50 p-3 rounded border">
                <div className="text-sm text-gray-600">最高分数</div>
                <div className="text-2xl font-bold text-green-600">
                  {aiStats.bestScore}
                </div>
              </div>

              <div className="bg-purple-50 p-3 rounded border">
                <div className="text-sm text-gray-600">最近得分</div>
                <div className="text-lg font-semibold text-purple-600">
                  {aiStats.currentScore}
                </div>
              </div>

              <div className="bg-orange-50 p-3 rounded border">
                <div className="text-sm text-gray-600">平均分数</div>
                <div className="text-lg font-semibold text-orange-600">
                  {aiStats.recentScores.length > 0
                    ? (
                        aiStats.recentScores.reduce((a, b) => a + b, 0) /
                        aiStats.recentScores.length
                      ).toFixed(1)
                    : "0.0"}
                </div>
              </div>
            </div>

            {/* 新增：适应性谨慎程度显示 */}
            {/* {trainStyle === "bold" && aiStats.episode > 0 && (
              <div className="mt-4 bg-yellow-50 p-3 rounded border">
                <div className="text-sm font-semibold text-yellow-700 mb-2">
                  🧠 AI的适应性学习状态：
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-yellow-600">谨慎程度：</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${
                              (aiStats.adaptiveCautionLevel || 0) * 100
                            }%`,
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-mono">
                        {((aiStats.adaptiveCautionLevel || 0) * 100).toFixed(0)}
                        %
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-yellow-600">
                      最近失败率：
                    </span>
                    <span className="text-sm font-mono">
                      {aiStats.recentFailures &&
                      aiStats.recentFailures.length > 0
                        ? `${(
                            (aiStats.recentFailures.filter((f) => f).length /
                              aiStats.recentFailures.length) *
                            100
                          ).toFixed(0)}%`
                        : "0%"}
                    </span>
                  </div>
                  <div className="text-xs text-yellow-600 mt-1">
                    {aiStats.adaptiveCautionLevel > 0.3
                      ? "🛡️ AI变得谨慎了，最近失败太多"
                      : aiStats.adaptiveCautionLevel > 0.1
                      ? "⚖️ AI正在平衡勇敢与谨慎"
                      : "🚀 AI处于勇敢探索状态"}
                  </div>
                </div>
              </div>
            )} */}
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              📊 AI的学习进度
            </h2>

            <div className="bg-blue-50 p-3 rounded border">
              <div className="text-sm text-gray-600">🎯 AI探索了哪些分数？</div>
              <div className="space-y-2 mt-2">
                {Object.entries(explorationStats).map(([state, visits]) => (
                  <div key={state} className="flex items-center">
                    <span className="w-16 text-sm font-medium">{state}分:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-3 mx-2">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${
                          parseInt(state) >= 8
                            ? "bg-red-500"
                            : parseInt(state) >= 5
                            ? "bg-yellow-500"
                            : "bg-blue-500"
                        }`}
                        style={{
                          width: `${Math.min(
                            100,
                            (visits / Math.max(1, aiStats.episode)) * 100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <span className="w-16 text-xs text-gray-600">
                      {visits}次
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">🤔 AI的想法</h2>
          <p className="text-gray-600 mb-4">
            仔细观察，猜测下方数字对AI选择，有什么作用？
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-3 border border-gray-300 text-lg">
                    当前分数
                  </th>
                  <th className="p-3 border border-gray-300 text-red-600 text-lg">
                    🏁 结束游戏
                  </th>
                  <th className="p-3 border border-gray-300 text-blue-600 text-lg">
                    🎲 继续翻牌
                  </th>
                  <th className="p-3 border border-gray-300 text-lg">
                    AI的选择
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(qTable).map(([state, actions]) => {
                  const bestAction =
                    actions["结束游戏"] > actions["继续翻牌"]
                      ? "结束游戏"
                      : "继续翻牌";
                  const isBalanced =
                    Math.abs(actions["结束游戏"] - actions["继续翻牌"]) < 0.1;
                  const maxValue = Math.max(
                    Math.abs(actions["结束游戏"]),
                    Math.abs(actions["继续翻牌"])
                  );
                  const isLearned = maxValue > 0.5;

                  return (
                    <tr
                      key={state}
                      className={`hover:bg-gray-50 ${
                        isLearned ? "" : "opacity-50"
                      }`}
                    >
                      <td className="p-3 border border-gray-300 text-center">
                        <span className="text-2xl font-bold">{state}分</span>
                      </td>
                      <td className="p-3 border border-gray-300 text-center">
                        <span
                          className={`text-lg font-bold ${
                            actions["结束游戏"] > actions["继续翻牌"]
                              ? "text-red-600 bg-red-100 px-2 py-1 rounded"
                              : "text-gray-600"
                          }`}
                        >
                          {actions["结束游戏"].toFixed(2)}
                        </span>
                      </td>
                      <td className="p-3 border border-gray-300 text-center">
                        <span
                          className={`text-lg font-bold ${
                            actions["继续翻牌"] > actions["结束游戏"]
                              ? "text-blue-600 bg-blue-100 px-2 py-1 rounded"
                              : "text-gray-600"
                          }`}
                        >
                          {actions["继续翻牌"].toFixed(2)}
                        </span>
                      </td>
                      <td className="p-3 border border-gray-300 text-center">
                        <span
                          className={`text-lg font-bold px-3 py-1 rounded ${
                            !isLearned
                              ? "text-gray-400"
                              : isBalanced
                              ? "text-gray-600 bg-gray-100"
                              : bestAction === "结束游戏"
                              ? "text-red-600 bg-red-100"
                              : "text-blue-600 bg-blue-100"
                          }`}
                        >
                          {!isLearned
                            ? "🤷 还没学"
                            : isBalanced
                            ? "🤔 纠结中"
                            : bestAction === "结束游戏"
                            ? "🏁 结束"
                            : "🎲 翻牌"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="bg-red-50 p-3 rounded border">
              <div className="font-semibold text-red-700">🏁 选择结束游戏</div>
              <div className="text-red-600">保住当前分数</div>
            </div>
            <div className="bg-blue-50 p-3 rounded border">
              <div className="font-semibold text-blue-700">🎲 选择继续翻牌</div>
              <div className="text-blue-600">冒险争取更高分</div>
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
}
